import { 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter, 
  updateDoc, 
  increment,
  addDoc,
  deleteDoc,
  Timestamp, 
  QueryDocumentSnapshot
} from 'firebase/firestore';

import { 
  UserRole, 
  UserPreferences,
  type User,
  type UserFavorite,
  type UserDownload,
  type Wallpaper,
  usersCollection, 
  wallpapersCollection, 
  getUserFavoritesCollection, 
  getUserDownloadsCollection 
} from '../schema';

export class DatabaseService {
  
  // Wallpaper operations
  async getWallpapers(
    categoryFilter?: string, 
    lastDoc?: QueryDocumentSnapshot, 
    limitCount = 20
  ): Promise<{ wallpapers: Wallpaper[], lastDoc: QueryDocumentSnapshot | null }> {
    try {
      let q = query(wallpapersCollection, orderBy('createdAt', 'desc'), limit(limitCount));
      
      if (categoryFilter) {
        q = query(wallpapersCollection, 
          where('category', '==', categoryFilter), 
          orderBy('createdAt', 'desc'), 
          limit(limitCount)
        );
      }
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const snapshot = await getDocs(q);
      const wallpapers = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Wallpaper[];
      
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      
      return { wallpapers, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error fetching wallpapers:', error);
      throw error;
    }
  }
  
  async getWallpaperById(wallpaperId: string): Promise<Wallpaper | null> {
    try {
      const docRef = doc(wallpapersCollection, wallpaperId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          id: docSnap.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as Wallpaper;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching wallpaper:', error);
      throw error;
    }
  }
  
  async searchWallpapers(
    searchTerm: string, 
    lastDoc?: QueryDocumentSnapshot, 
    limitCount = 20
  ): Promise<{ wallpapers: Wallpaper[], lastDoc: QueryDocumentSnapshot | null }> {
    try {
      // Convert search term to lowercase for case-insensitive search
      const searchKeywords = searchTerm.toLowerCase().split(' ').filter(term => term.length > 0);
      
      let q = query(
        wallpapersCollection,
        where('searchKeywords', 'array-contains-any', searchKeywords),
        orderBy('downloadCount', 'desc'),
        limit(limitCount)
      );
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const snapshot = await getDocs(q);
      const wallpapers = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date()
      })) as Wallpaper[];
      
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      
      return { wallpapers, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error searching wallpapers:', error);
      throw error;
    }
  }
  
  async incrementDownloadCount(wallpaperId: string): Promise<void> {
    try {
      const wallpaperRef = doc(wallpapersCollection, wallpaperId);
      await updateDoc(wallpaperRef, {
        downloadCount: increment(1),
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error incrementing download count:', error);
      throw error;
    }
  }
  
  // User operations
  async createUserProfile(
    userId: string, 
    userData: {
      displayName?: string | null;
      email?: string | null;
      photoURL?: string | null;
      preferences?: Partial<UserPreferences>;
      role?: UserRole;
      createdAt?: Date;
      lastActive?: Date;
    } = {}
  ): Promise<void> {
    try {
      const userRef = doc(usersCollection, userId);
      
      // Default preferences
      const defaultPreferences: UserPreferences = {
        preferredResolution: '1920x1080',
        adConsent: false,
        theme: 'light'
      };
      
      // Merge provided preferences with defaults
      const mergedPreferences: UserPreferences = {
        ...defaultPreferences,
        ...userData.preferences
      };
      
      // Create user with all required fields
      const userWithId: User = {
        id: userId,
        displayName: userData.displayName || null,
        email: userData.email || null,
        photoURL: userData.photoURL || null,
        preferences: mergedPreferences,
        role: userData.role || UserRole.USER,
        createdAt: userData.createdAt || new Date(),
        lastActive: userData.lastActive || new Date()
      };
      
      await setDoc(userRef, {
        ...userWithId,
        createdAt: Timestamp.now(),
        lastActive: Timestamp.now()
      });
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }
  
  async getUserProfile(userId: string): Promise<User | null> {
    try {
      const userRef = doc(usersCollection, userId);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const data = userSnap.data();
        return {
          ...data,
          id: userSnap.id,
          createdAt: data.createdAt?.toDate() || new Date(),
          lastActive: data.lastActive?.toDate() || new Date()
        } as User;
      }
      
      return null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }
  
  async updateUserLastActive(userId: string): Promise<void> {
    try {
      const userRef = doc(usersCollection, userId);
      await updateDoc(userRef, {
        lastActive: Timestamp.now()
      });
    } catch (error) {
      console.error('Error updating user last active:', error);
      throw error;
    }
  }
  
  // Favorites operations
  async addToFavorites(userId: string, wallpaperId: string): Promise<void> {
    try {
      const favoritesCollection = getUserFavoritesCollection(userId);
      const favoriteRef = doc(favoritesCollection, wallpaperId);
      
      await setDoc(favoriteRef, {
        id: wallpaperId,
        addedAt: Timestamp.now()
      });
    } catch (error) {
      console.error('Error adding to favorites:', error);
      throw error;
    }
  }
  
  async removeFromFavorites(userId: string, wallpaperId: string): Promise<void> {
    try {
      const favoritesCollection = getUserFavoritesCollection(userId);
      const favoriteRef = doc(favoritesCollection, wallpaperId);
      
      await deleteDoc(favoriteRef);
    } catch (error) {
      console.error('Error removing from favorites:', error);
      throw error;
    }
  }
  
  async getUserFavorites(
    userId: string, 
    lastDoc?: QueryDocumentSnapshot, 
    limitCount = 20
  ): Promise<{ favorites: UserFavorite[], lastDoc: QueryDocumentSnapshot | null }> {
    try {
      const favoritesCollection = getUserFavoritesCollection(userId);
      let q = query(favoritesCollection, orderBy('addedAt', 'desc'), limit(limitCount));
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const snapshot = await getDocs(q);
      const favorites = snapshot.docs.map(doc => ({
        ...doc.data(),
        addedAt: doc.data().addedAt?.toDate() || new Date()
      })) as UserFavorite[];
      
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      
      return { favorites, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error fetching user favorites:', error);
      throw error;
    }
  }
  
  async isFavorite(userId: string, wallpaperId: string): Promise<boolean> {
    try {
      const favoritesCollection = getUserFavoritesCollection(userId);
      const favoriteRef = doc(favoritesCollection, wallpaperId);
      const favoriteSnap = await getDoc(favoriteRef);
      
      return favoriteSnap.exists();
    } catch (error) {
      console.error('Error checking if wallpaper is favorite:', error);
      return false;
    }
  }
  
  // Download history operations
  async addToDownloadHistory(userId: string, wallpaperId: string, resolution: string): Promise<void> {
    try {
      const downloadsCollection = getUserDownloadsCollection(userId);
      
      await addDoc(downloadsCollection, {
        wallpaperId,
        resolution,
        downloadedAt: Timestamp.now()
      });
      
      // Also increment the wallpaper's download count
      await this.incrementDownloadCount(wallpaperId);
    } catch (error) {
      console.error('Error adding to download history:', error);
      throw error;
    }
  }
  
  async getUserDownloadHistory(
    userId: string, 
    lastDoc?: QueryDocumentSnapshot, 
    limitCount = 20
  ): Promise<{ downloads: UserDownload[], lastDoc: QueryDocumentSnapshot | null }> {
    try {
      const downloadsCollection = getUserDownloadsCollection(userId);
      let q = query(downloadsCollection, orderBy('downloadedAt', 'desc'), limit(limitCount));
      
      if (lastDoc) {
        q = query(q, startAfter(lastDoc));
      }
      
      const snapshot = await getDocs(q);
      const downloads = snapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        downloadedAt: doc.data().downloadedAt?.toDate() || new Date()
      })) as UserDownload[];
      
      const newLastDoc = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
      
      return { downloads, lastDoc: newLastDoc };
    } catch (error) {
      console.error('Error fetching user download history:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const databaseService = new DatabaseService();